/* istanbul ignore file */
/* tslint:disable */
/* eslint-disable */
import type { BaseMaterial } from '../models/BaseMaterial';
import type { BillOfMaterials } from '../models/BillOfMaterials';
import type { Configuration } from '../models/Configuration';
import type { Currency } from '../models/Currency';
import type { ExtraheightAndOverhang } from '../models/ExtraheightAndOverhang';
import type { Material } from '../models/Material';
import type { MaterialFolder } from '../models/MaterialFolder';
import type { MaterialType } from '../models/MaterialType';
import type { NewConfiguration } from '../models/NewConfiguration';
import type { PostConfigurationResponse } from '../models/PostConfigurationResponse';
import type { Product } from '../models/Product';
import type { Style } from '../models/Style';
import type { StyleListModel } from '../models/StyleListModel';
import type { WIPConfiguration } from '../models/WIPConfiguration';
import type { CancelablePromise } from '../core/CancelablePromise';
import { request as __request } from '../core/request';

export class DevelopersService {

    /**
     * returns all the products
     * Returns all available products in the system
     * @returns Product available products
     * @throws ApiError
     */
    public static getAllProducts(): CancelablePromise<Array<Product>> {
        return __request({
            method: 'GET',
            path: `/products`,
            errors: {
                400: `Validation Error`,
                404: `Not Found Error`,
            },
        });
    }

    /**
     * returns only one product
     * Returns information about the product
     * @param productId The id of the product
     * @returns Product Summary of the product
     * @throws ApiError
     */
    public static getProductById(
        productId: string,
    ): CancelablePromise<Product> {
        return __request({
            method: 'GET',
            path: `/products/${productId}`,
            errors: {
                400: `Validation Error`,
                404: `Not Found Error`,
            },
        });
    }

    /**
     * returns all the materials for a given product
     * Returns all the materials for a given product
     * @param productId The id of the product
     * @returns any available materials( roofing, trims, siding )
     * @throws ApiError
     */
    public static getMaterialsForProduct(
        productId: string,
    ): CancelablePromise<{
        materials: Array<Material>;
        materialTypes: Array<MaterialType>;
        materialFolders: Array<MaterialFolder>;
        baseMaterials?: Array<BaseMaterial>;
    }> {
        return __request({
            method: 'GET',
            path: `/materials`,
            query: {
                'productId': productId,
            },
            errors: {
                400: `Validation Error`,
                404: `Not Found Error`,
            },
        });
    }

    /**
     * returns all the styles for a given product in simple form
     * Returns all the styles for a given product in simple form
     * @param productId The id of the product
     * @returns StyleListModel available styles in a simple form for the given product id
     * @throws ApiError
     */
    public static getStylesForProduct(
        productId: string,
    ): CancelablePromise<Array<StyleListModel>> {
        return __request({
            method: 'GET',
            path: `/styles`,
            query: {
                'productId': productId,
            },
            errors: {
                400: `Validation Error`,
                404: `Not Found Error`,
            },
        });
    }

    /**
     * Returns all the info for the given style and product id
     * Returns all the info for the given style and product id
     * @param productId The id of the product
     * @param styleId The id of the style
     * @param currency The currency in which the prices will be displayed
     * @returns Style All the info available for this specific style and product
     * @throws ApiError
     */
    public static getStyle(
        productId: string,
        styleId: string,
        currency: Currency,
    ): CancelablePromise<Style> {
        return __request({
            method: 'GET',
            path: `/style`,
            query: {
                'productId': productId,
                'styleId': styleId,
                'currency': currency,
            },
            errors: {
                400: `Validation Error`,
                404: `Not Found Error`,
            },
        });
    }

    /**
     * Returns extraheight and overhang info with prices for the given style, width and depth
     * Returns extraheight and overhang info with prices for the given style, width and depth
     * @param width The width chosen by the user
     * @param depth The depth chosen by the user
     * @returns ExtraheightAndOverhang All the info available for this specific style and width/depth
     * @throws ApiError
     */
    public static getExtraheightAndOverhang(
        width: string,
        depth: string,
    ): CancelablePromise<ExtraheightAndOverhang> {
        return __request({
            method: 'GET',
            path: `/extraheight_and_overhang`,
            query: {
                'width': width,
                'depth': depth,
            },
            errors: {
                400: `Validation Error`,
                404: `Not Found Error`,
            },
        });
    }

    /**
     * Returns all the info for the given configuration id
     * Returns all the info for the given configuration id
     * @param id The id of the configuration
     * @param productId The id of the product
     * @param currency The currency in which the prices will be displayed
     * @returns Configuration All the requiered information for rendering an existing configuration
     * @throws ApiError
     */
    public static getConfiguration(
        id: string,
        productId: string,
        currency: Currency,
    ): CancelablePromise<Configuration> {
        return __request({
            method: 'GET',
            path: `/configuration`,
            query: {
                'id': id,
                'productId': productId,
                'currency': currency,
            },
            errors: {
                400: `Validation Error`,
                404: `Not Found Error`,
            },
        });
    }

    /**
     * Saves the configuration info in the database
     * Saves the configuration info in the database
     * @param requestBody
     * @returns PostConfigurationResponse Configuration
     * @throws ApiError
     */
    public static saveConfiguration(
        requestBody: NewConfiguration,
    ): CancelablePromise<PostConfigurationResponse> {
        return __request({
            method: 'POST',
            path: `/configuration`,
            body: requestBody,
            mediaType: 'application/json',
            errors: {
                400: `Validation Error`,
            },
        });
    }

    /**
     * Returns the updated bill of materials when the user updates tthe scene
     * Returns the updated bill of materials when the user updates tthe scene
     * @param requestBody
     * @returns BillOfMaterials All the requiered information for rendering an existing configuration
     * @throws ApiError
     */
    public static getBillOfMaterials(
        requestBody: WIPConfiguration,
    ): CancelablePromise<BillOfMaterials> {
        return __request({
            method: 'POST',
            path: `/billOfMaterials`,
            body: requestBody,
            mediaType: 'application/json',
            errors: {
                400: `Validation Error`,
                404: `Not Found Error`,
            },
        });
    }

}